import {Component, forwardRef, Input, OnInit, OnDestroy} from '@angular/core';
import {AbstractControl, ControlValueAccessor, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator} from '@angular/forms';
import {NgbTimeStruct} from '@ng-bootstrap/ng-bootstrap';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

export interface StartEndTime {
  startTime: NgbTimeStruct;
  endTime: NgbTimeStruct;
  excludeWeekends: boolean;
}

@Component({
  selector: 'app-start-end-time',
  templateUrl: './start-end-time.component.html',
  styleUrls: ['./start-end-time.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => StartEndTimeComponent)},
    {provide: NG_VALIDATORS, multi: true, useExisting: forwardRef(() => StartEndTimeComponent)}
  ],
  standalone: false
})
export class StartEndTimeComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {

  @Input() readonly;

  id: string = this.getId();

  @Input()
  model: StartEndTime;

  form: UntypedFormGroup;
  private ngUnsubscribe = new Subject<void>();

  constructor(private fb: UntypedFormBuilder) {}

  static validateTime(c: UntypedFormControl): {[key: string]: any} {
    const time: NgbTimeStruct = c.value;
    if (time && time.hour !== undefined && time.minute !== undefined) {
      return null;
    } else {
      return {invalidTime: true};
    }
  }

  static validateStartBeforeEnd(g: UntypedFormGroup): ValidationErrors | null {
    const start: NgbTimeStruct = g.get('startTime').value;
    const end: NgbTimeStruct = g.get('endTime').value;
    if (!start || !end) {
      return null;
    }
    const startTime = start.hour === 0 ? start.minute : (start.hour * 60) + start.minute;
    const endTime = end.hour === 0 ? end.minute : (end.hour * 60) + end.minute;
    if (startTime <= endTime) {
      return null;
    } else {
      return {endBeforeStart: true};
    }
  }

  static getEmptyModel(): StartEndTime {
    return {
      startTime: { hour: 0, minute: 0, second: 0 },
      endTime: { hour: 0, minute: 0, second: 0 },
      excludeWeekends: false,
    };
  }

  onChange = (_) => {};
  onTouched = () => {};

  ngOnInit() {
    this.model = this.model || StartEndTimeComponent.getEmptyModel();
    this.form = this.fb.group({
        startTime: [this.model.startTime, StartEndTimeComponent.validateTime],
        endTime: [this.model.endTime, StartEndTimeComponent.validateTime],
        excludeWeekends: this.model.excludeWeekends
      }, {validators: StartEndTimeComponent.validateStartBeforeEnd}
    );
    this.form.get('startTime').valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event$) => this.onChangeStartTime(event$));
    this.form.get('endTime').valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event$) => this.onChangeEndTime(event$));
    this.form.get('excludeWeekends').valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event$) => this.onChangeExcludeWeekends(event$));
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  writeValue(model: StartEndTime): void {
    if (model) {
      this.model = model;
    } else {
      this.model = StartEndTimeComponent.getEmptyModel();
    }
    this.form.get('startTime').setValue(this.model.startTime, {emitEvent: false});
    this.form.get('endTime').setValue(this.model.endTime, {emitEvent: false});
    this.form.get('excludeWeekends').setValue(this.model.excludeWeekends, {emitEvent: false});
  }

  registerOnChange(fn: any): void {
    this.form.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(fn);
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.get('startTime').disable({emitEvent: false});
      this.form.get('endTime').disable({emitEvent: false});
      this.form.get('excludeWeekends').disable({emitEvent: false});
    } else {
      this.form.get('startTime').enable({emitEvent: false});
      this.form.get('endTime').enable({emitEvent: false});
      this.form.get('excludeWeekends').enable({emitEvent: false});
    }
  }

  isFormTouched() {
    return this.form.invalid && (this.form.dirty || this.form.touched);
  }

  isTouched(name: string): boolean {
    return this.form.get(name).invalid && (this.form.get(name).dirty || this.form.get(name).touched);
  }

  validate(c: AbstractControl) {
    const errors: ValidationErrors = [];
    if (this.form.get('startTime').errors) {
      errors.push(this.form.get('startTime').errors);
    }
    if (this.form.get('endTime').errors) {
      errors.push(this.form.get('endTime').errors);
    }
    if (this.form.errors) {
      errors.push(this.form.errors);
    }
    return errors.length > 0 ? errors : null;
  }

  onChangeStartTime(event: NgbTimeStruct) {
    this.model.startTime = event;
    this.onChange(this.model);
  }

  onChangeEndTime(event: NgbTimeStruct) {
    this.model.endTime = event;
    this.onChange(this.model);
  }

  onChangeExcludeWeekends(event: boolean) {
    this.model.excludeWeekends = event;
    this.onChange(this.model);
  }

  getFormValidationErrors(form: UntypedFormGroup): ValidationErrors {
    const errors: ValidationErrors = [];
    Object.keys(form.controls).forEach(key => {
      const controlErrors: ValidationErrors = this.form.get(key).errors;
      if (controlErrors != null) {
        errors.push(controlErrors);
      }
    });
    return errors;
  }

  getId() {
    return Math.random().toString(16).slice(2);
  }
}
